import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from './axiosInstance';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, DeleteOutline, EditOutlined, Search } from '@mui/icons-material';
import './ExportGridView.css';

const columnWidths = {
  job_title: '46%',
  is_labour: '16%',
  standard_daily_hours: '18%',
  actions: '20%',
};

const defaultFormState = {
  job_title: '',
  is_labour: false,
  standard_daily_hours: '9',
};

const labourFilterOptions = [
  { value: 'all', label: 'All Titles' },
  { value: 'labour', label: 'Labour' },
  { value: 'non_labour', label: 'Non-Labour' },
];

const ManageJobTitlesPage = () => {
  const [records, setRecords] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [labourFilter, setLabourFilter] = useState('all');
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [pageInput, setPageInput] = useState('1');
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortKey, setSortKey] = useState('job_title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [originalNormalizedJobTitle, setOriginalNormalizedJobTitle] = useState('');
  const [formData, setFormData] = useState(defaultFormState);
  const [formError, setFormError] = useState('');
  const [savingLabourKey, setSavingLabourKey] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingJobTitle, setDeletingJobTitle] = useState('');
  const previousSearchQueryRef = useRef('');

  const showToast = useCallback((message, severity = 'success') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('hv-toast', { detail: { message, severity } }));
  }, []);

  const loadRecords = useCallback(async (
    pageIndex = page,
    nextSortKey = sortKey,
    nextSortDirection = sortDirection,
    nextSearchQuery = searchQuery,
    nextLabourFilter = labourFilter,
  ) => {
    setLoadingRecords(true);
    try {
      const response = await axiosInstance.get('/api/v1/manage/job-titles', {
        params: {
          page: pageIndex + 1,
          limit: rowsPerPage,
          sort_key: nextSortKey,
          sort_direction: nextSortDirection,
          ...(nextSearchQuery?.trim() ? { search: nextSearchQuery.trim() } : {}),
          ...(nextLabourFilter !== 'all' ? { labour: nextLabourFilter } : {}),
        },
      });
      const payload = response?.data ?? {};
      const nextRecords = Array.isArray(payload?.data) ? payload.data : [];
      setRecords(nextRecords);
      setTotalRecords(Number(payload?.total ?? nextRecords.length ?? 0));
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load job titles.', 'error');
    } finally {
      setLoadingRecords(false);
    }
  }, [labourFilter, page, rowsPerPage, searchQuery, showToast, sortDirection, sortKey]);

  useEffect(() => {
    if (previousSearchQueryRef.current !== searchQuery) {
      previousSearchQueryRef.current = searchQuery;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    loadRecords(page, sortKey, sortDirection, searchQuery, labourFilter);
  }, [labourFilter, loadRecords, page, sortKey, sortDirection, searchQuery]);

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page, totalRecords]);

  const closeDialog = () => {
    if (savingRecord) return;
    setDialogOpen(false);
    setFormError('');
    setFormData(defaultFormState);
    setFormMode('create');
    setOriginalNormalizedJobTitle('');
  };

  const openCreateDialog = () => {
    setFormMode('create');
    setOriginalNormalizedJobTitle('');
    setFormData(defaultFormState);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    setFormMode('edit');
    setOriginalNormalizedJobTitle(record?.normalized_job_title || '');
    setFormData({
      job_title: record?.job_title || '',
      is_labour: Boolean(record?.is_labour),
      standard_daily_hours: record?.standard_daily_hours === undefined || record?.standard_daily_hours === null
        ? ''
        : String(record.standard_daily_hours),
    });
    setFormError('');
    setDialogOpen(true);
  };

  const openDeleteDialog = (record) => {
    setDeleteTarget(record);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingJobTitle) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleFormLabourChange = (checked) => {
    setFormData((current) => {
      const previousDefaultHours = current.is_labour ? '10' : '9';
      const nextDefaultHours = checked ? '10' : '9';
      const shouldUseNextDefault = !String(current.standard_daily_hours || '').trim() || String(current.standard_daily_hours) === previousDefaultHours;
      return {
        ...current,
        is_labour: checked,
        standard_daily_hours: shouldUseNextDefault ? nextDefaultHours : current.standard_daily_hours,
      };
    });
  };

  const getRecordKey = (record) => record?.normalized_job_title || record?.id || record?.job_title || '';

  const handleLabourToggleChange = async (record, checked) => {
    const recordKey = getRecordKey(record);

    setSavingLabourKey(recordKey);
    try {
      const payload = {
        job_title: record?.job_title || '',
        is_labour: checked,
      };
      if (record?.standard_daily_hours !== undefined && record?.standard_daily_hours !== null) {
        payload.standard_daily_hours = record.standard_daily_hours;
      }
      await axiosInstance.put(`/api/v1/manage/job-titles/${encodeURIComponent(record?.normalized_job_title || '')}`, payload);
      setRecords((current) => current.map((item) => (
        getRecordKey(item) === recordKey
          ? { ...item, is_labour: checked }
          : item
      )));
      showToast('Job title updated successfully.', 'success');
      await loadRecords(page, sortKey, sortDirection, searchQuery, labourFilter);
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to update job title.';
      showToast(message, 'error');
      await loadRecords(page, sortKey, sortDirection, searchQuery, labourFilter);
    } finally {
      setSavingLabourKey('');
    }
  };

  const handleDelete = async () => {
    const normalizedJobTitle = deleteTarget?.normalized_job_title || '';
    if (!normalizedJobTitle) return;

    setDeletingJobTitle(normalizedJobTitle);
    try {
      await axiosInstance.delete(`/api/v1/manage/job-titles/${encodeURIComponent(normalizedJobTitle)}`);
      showToast('Job title deleted successfully.', 'success');
      closeDeleteDialog();
      await loadRecords(page, sortKey, sortDirection, searchQuery, labourFilter);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to delete job title.', 'error');
    } finally {
      setDeletingJobTitle('');
    }
  };

  const validateForm = () => {
    if (!formData.job_title.trim()) return 'Job title is required.';
    if (String(formData.standard_daily_hours || '').trim()) {
      const standardDailyHours = Number(formData.standard_daily_hours);
      if (!Number.isFinite(standardDailyHours)) return 'Standard daily hours must be a number.';
      if (standardDailyHours < 0) return 'Standard daily hours cannot be negative.';
    }
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSavingRecord(true);
    setFormError('');
    try {
      const payload = {
        job_title: formData.job_title.trim(),
        is_labour: formData.is_labour,
      };
      if (String(formData.standard_daily_hours || '').trim()) {
        payload.standard_daily_hours = Number(formData.standard_daily_hours);
      }

      if (formMode === 'edit' && originalNormalizedJobTitle) {
        await axiosInstance.put(`/api/v1/manage/job-titles/${encodeURIComponent(originalNormalizedJobTitle)}`, payload);
      } else {
        await axiosInstance.post('/api/v1/manage/job-titles', payload);
      }

      showToast(formMode === 'edit' ? 'Job title updated successfully.' : 'Job title created successfully.', 'success');
      closeDialog();
      await loadRecords(page, sortKey, sortDirection, searchQuery, labourFilter);
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to save job title.';
      setFormError(message);
      showToast(message, 'error');
    } finally {
      setSavingRecord(false);
    }
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage === page) return;
    setPage(newPage);
  };

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!totalRecords) {
      setPageInput('1');
      return;
    }
    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(page + 1));
      return;
    }
    const totalPages = Math.max(Math.ceil(totalRecords / rowsPerPage), 1);
    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages);
    if (clampedPage !== page + 1) {
      setPage(clampedPage - 1);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleSortChange = (columnKey) => {
    const nextDirection = sortKey === columnKey ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortKey(columnKey);
    setSortDirection(nextDirection);
    setPage(0);
  };

  const applySearch = useCallback(() => {
    setPage(0);
    setSearchQuery(searchInput.trim());
  }, [searchInput]);

  const handleLabourFilterChange = (event) => {
    setLabourFilter(event.target.value);
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applySearch();
  };

  const hasActiveFilters = Boolean(searchInput || searchQuery || labourFilter !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setLabourFilter('all');
    setPage(0);
  };

  const renderSortIcon = (columnKey) => {
    if (sortKey !== columnKey) return null;
    return sortDirection === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', textAlign: 'left' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5, fontWeight: 500 }}>
              Job Titles
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
          <TextField
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            label="Search Job Titles"
            size="small"
            placeholder="Search title"
            sx={{ minWidth: 320 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="search job titles" edge="end" onClick={applySearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="job-title-labour-filter-label">Labour</InputLabel>
            <Select
              labelId="job-title-labour-filter-label"
              value={labourFilter}
              label="Labour"
              onChange={handleLabourFilterChange}
            >
              {labourFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            Add Job Title
          </Button>
          </Stack>
        </Box>

        <TableContainer sx={{ position: 'relative', maxHeight: '68vh', minHeight: loadingRecords && !records.length ? 200 : 'auto', mt: 2, overflowX: 'auto', overflowY: 'auto' }}>
          {loadingRecords && records.length > 0 && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: 'rgba(255, 255, 255, 0.82)', backdropFilter: 'blur(1px)', pointerEvents: 'none', zIndex: 3 }}>
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body2" color="text.secondary">Loading next page...</Typography>
            </Box>
          )}
          <Table
            stickyHeader
            size="small"
            sx={{
              '& .MuiTableCell-sizeSmall': {
                padding: '8px 16px',
                height: 44,
                boxSizing: 'border-box',
              },
              '& .MuiTableHead .MuiTableCell-sizeSmall': {
                padding: '12px 16px',
                height: 48,
              },
              '& .MuiInputBase-root.MuiInputBase-sizeSmall': {
                minHeight: 28,
              },
              '& .MuiSelect-select.MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
              },
              '& .MuiButton-sizeSmall': {
                minHeight: 28,
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell className={`sortableHeader ${sortKey === 'job_title' ? 'sorted' : ''}`} sx={{ width: columnWidths.job_title, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('job_title')} role="button" tabIndex={0}>
                    <strong>Job Title</strong>
                    {renderSortIcon('job_title')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'is_labour' ? 'sorted' : ''}`} sx={{ width: columnWidths.is_labour, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('is_labour')} role="button" tabIndex={0}>
                    <strong>Labour</strong>
                    {renderSortIcon('is_labour')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'standard_daily_hours' ? 'sorted' : ''}`} sx={{ width: columnWidths.standard_daily_hours, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('standard_daily_hours')} role="button" tabIndex={0}>
                    <strong>Standard Daily Hours</strong>
                    {renderSortIcon('standard_daily_hours')}
                  </div>
                </TableCell>
                <TableCell className="sortableHeader" sx={{ width: columnWidths.actions, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent"><strong>Actions</strong></div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRecords && !records.length ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={4} />
                      <Typography color="text.secondary">Loading...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : records.length > 0 ? (
                records.map((record) => (
                  <TableRow key={record?.id || record?.normalized_job_title} hover>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record?.job_title || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Switch
                        checked={Boolean(record?.is_labour)}
                        onChange={(event) => handleLabourToggleChange(record, event.target.checked)}
                        size="small"
                        disabled={savingLabourKey === getRecordKey(record)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{record?.standard_daily_hours ?? '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(record)} aria-label="edit job title">
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(record)} aria-label="delete job title">
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>No job titles found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <TextField value={pageInput} onChange={handlePageInputChange} onKeyDown={handlePageInputKeyDown} label="Page" size="small" sx={{ width: 90 }} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} disabled={loadingRecords || !totalRecords} />
            <Typography variant="body2" color="text.secondary">{totalRecords ? `of ${Math.max(Math.ceil(totalRecords / rowsPerPage), 1)}` : ''}</Typography>
            <Typography variant="body2" color="text.secondary">{totalRecords ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRecords)} of ${totalRecords}` : 'No records to show'}</Typography>
          </Box>
          <TablePagination component="div" count={totalRecords} page={page} onPageChange={handlePageChange} rowsPerPage={rowsPerPage} rowsPerPageOptions={[rowsPerPage]} showFirstButton showLastButton labelRowsPerPage="" labelDisplayedRows={() => ''} sx={{ '.MuiTablePagination-toolbar': { p: 0 }, '.MuiTablePagination-selectLabel': { display: 'none' }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{formMode === 'edit' ? 'Edit Job Title' : 'Add Job Title'}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'grid', gap: 2 }}>
          <TextField fullWidth label="Job Title" value={formData.job_title} onChange={(event) => handleFormChange('job_title', event.target.value)} error={Boolean(formError)} helperText={formError || ' '} />
          <TextField
            fullWidth
            label="Standard Daily Hours"
            type="number"
            value={formData.standard_daily_hours}
            onChange={(event) => handleFormChange('standard_daily_hours', event.target.value)}
            inputProps={{ min: 0, step: 0.25 }}
            helperText="Expected daily hours for this job title. Leave blank to use the backend default."
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Labour</Typography>
            <Switch checked={Boolean(formData.is_labour)} onChange={(event) => handleFormLabourChange(event.target.checked)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={savingRecord}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={savingRecord}>{savingRecord ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Job Title</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete job title {deleteTarget?.job_title || '-'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingJobTitle)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={Boolean(deletingJobTitle)}>
            {deletingJobTitle ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageJobTitlesPage;
